/**
 * Web TermDefinition Component
 */

import { useCallback, useContext, useRef, useState } from 'react'
import type {
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from 'react'
import { clsx } from 'clsx'
import Popover from '../popover/Popover'
import useId from '../../shared/helpers/useId'
import type { SkeletonShow } from '../skeleton/Skeleton'
import { createSkeletonClass } from '../skeleton/SkeletonHelper'
import useTranslation from '../../shared/useTranslation'
import type { SpacingProps } from '../../shared/types'
import { useSpacing, removeSpaceProps } from '../space/SpacingUtils'
import Context from '../../shared/Context'
import { extendPropsWithContext } from '../../shared/component-helper'

export type TermDefinitionProps = {
  /**
   * Term shown as the trigger. Typically a short word or phrase.
   */
  children: ReactNode
  /**
   * Definition text that will be displayed inside.
   */
  content: ReactNode
  /**
   * Optional CSS class for the anchor trigger.
   */
  className?: string
  /**
   * Defines the preferred popover placement relative to the trigger.
   * Default: `"bottom"`
   */
  placement?: 'top' | 'right' | 'bottom' | 'left'
  /**
   * If set to `true`, an overlaying skeleton with animation will be shown.
   */
  skeleton?: SkeletonShow
}

export type TermDefinitionAllProps = TermDefinitionProps &
  SpacingProps &
  HTMLAttributes<HTMLSpanElement>

const termDefinitionDefaultProps: Partial<TermDefinitionAllProps> = {
  placement: 'bottom',
}

export default function TermDefinition(
  localProps: TermDefinitionAllProps
) {
  const context = useContext(Context)

  const allProps = extendPropsWithContext(
    localProps,
    termDefinitionDefaultProps,
    { skeleton: context?.skeleton },
    context?.TermDefinition
  )

  const { children, content, className, placement, skeleton, ...rest } =
    allProps

  const skeletonClasses = createSkeletonClass('font', skeleton, context)

  const [active, setActive] = useState(false)
  const triggerRef = useRef<HTMLSpanElement | null>(null)
  const id = useId()
  const { TermDefinition: tr = {} } = useTranslation()
  const title = active ? tr.closeTriggerTitle : tr.openTriggerTitle

  const toggle = useCallback((next?: boolean) => {
    setActive((prev) => {
      const value = typeof next === 'boolean' ? next : !prev
      if (prev && !value) {
        triggerRef.current?.focus({ preventScroll: true })
      }
      return value
    })
  }, [])

  const onClick = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.preventDefault()
      toggle()
    },
    [toggle]
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement> & KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggle()
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        toggle(false)
      }
    },
    [toggle]
  )

  const triggerProps = removeSpaceProps(rest)

  return (
    <>
      <span
        {...useSpacing(rest, {
          role: 'button',
          tabIndex: 0,
          ref: triggerRef,
          className: clsx(
            'dnb-term-definition__trigger',
            'dnb-anchor',
            active && 'dnb-anchor--hover',
            skeletonClasses,
            className
          ),
          'aria-expanded': active,
          'aria-controls': active ? id : undefined,
          'aria-describedby': `${id}-description`,
          title,
          onClick,
          onKeyDown,
          ...triggerProps,
        })}
      >
        {children}
      </span>

      <span className="dnb-sr-only" aria-hidden id={`${id}-description`}>
        {title}
      </span>

      <Popover
        id={id}
        targetElement={triggerRef}
        open={active}
        onOpenChange={toggle}
        showDelay={0}
        hideDelay={0}
        triggerOffset={4}
        placement={placement}
        arrowPosition="left"
        alignOnTarget="left"
        arrowEdgeOffset={0}
        className="dnb-term-definition"
        portalRootClass="dnb-term-definition__portal"
        omitDescribedBy
        hideArrow
        title={children}
      >
        {content}
      </Popover>
    </>
  )
}
